<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import PanelContainer from '@/components/PanelContainer.vue';
import type { SplitNode, BranchNode, PanelRegion, PanelSystem } from '@/composables/use-panels';

const props = defineProps<{
	node: SplitNode;
	panels: PanelSystem;
	region: PanelRegion;
	// pass through to PanelContainer leaf nodes
	autoHideTabs?: boolean;
	isDropTarget?: boolean;
	// when set, the content area of each leaf gets this explicit size in px (used by DockContainer to collapse content when minimized)
	contentSize?: number;
	// allowed split directions (DockContainer can restrict)
	allowedSplitDirections?: ('horizontal' | 'vertical')[];
}>();

defineEmits<{
	tabMouseUp: [uri: string, wasActive: boolean];
}>();

// --- resize state for branch nodes ---

const resizing = ref(false);
const resizeIndex = ref(-1);
let startPos = 0;
let startSizes: number[] = [];
let containerSize = 0;

const containerRef = ref<HTMLElement | null>(null);

const onResizePointerDown = (e: PointerEvent, index: number) => {
	if (props.node.type !== 'branch') return;
	e.preventDefault();

	const branch = props.node;
	resizing.value = true;
	resizeIndex.value = index;
	startPos = branch.direction === 'horizontal' ? e.clientX : e.clientY;
	startSizes = [...branch.sizes];

	// measure the container's total available size
	const el = containerRef.value;
	if (el) {
		containerSize = branch.direction === 'horizontal' ? el.offsetWidth : el.offsetHeight;
	}

	document.addEventListener('pointermove', onResizePointerMove);
	document.addEventListener('pointerup', onResizePointerUp);
	document.body.style.userSelect = 'none';
	document.body.style.cursor = branch.direction === 'horizontal' ? 'col-resize' : 'row-resize';
};

const onResizePointerMove = (e: PointerEvent) => {
	if (props.node.type !== 'branch' || resizeIndex.value < 0) return;
	const branch = props.node;

	const currentPos = branch.direction === 'horizontal' ? e.clientX : e.clientY;
	const delta = currentPos - startPos;

	// convert pixel delta to fraction of container
	if (containerSize <= 0) return;
	const deltaFraction = delta / containerSize;

	const i = resizeIndex.value;
	const prevSize = startSizes[i]!;
	const nextSize = startSizes[i + 1]!;
	const combined = prevSize + nextSize;

	// minimum fraction per pane (prevents collapsing to zero)
	const minFraction = 0.05;

	let newPrev = prevSize + deltaFraction;
	let newNext = nextSize - deltaFraction;

	// clamp
	if (newPrev < minFraction) {
		newPrev = minFraction;
		newNext = combined - minFraction;
	}
	if (newNext < minFraction) {
		newNext = minFraction;
		newPrev = combined - minFraction;
	}

	const newSizes = [...branch.sizes];
	newSizes[i] = newPrev;
	newSizes[i + 1] = newNext;

	props.panels.resizeSplit(branch.id, newSizes);
};

const onResizePointerUp = () => {
	resizing.value = false;
	resizeIndex.value = -1;
	document.removeEventListener('pointermove', onResizePointerMove);
	document.removeEventListener('pointerup', onResizePointerUp);
	document.body.style.userSelect = '';
	document.body.style.cursor = '';
	props.panels.commitResize();
};

onUnmounted(() => {
	document.removeEventListener('pointermove', onResizePointerMove);
	document.removeEventListener('pointerup', onResizePointerUp);
	document.body.style.userSelect = '';
	document.body.style.cursor = '';
});

// compute flex style for each child in a branch
const childStyle = (index: number): Record<string, string> => {
	if (props.node.type !== 'branch') return {};
	const size = props.node.sizes[index] ?? 0;
	return { flex: `${size} ${size} 0%`, minWidth: '0', minHeight: '0' };
};

const isHorizontal = computed(
	() => props.node.type === 'branch' && props.node.direction === 'horizontal',
);
</script>

<template>
	<!-- leaf node: render PanelContainer directly -->
	<PanelContainer
		v-if="node.type === 'leaf'"
		:node="node"
		:panels="panels"
		:region="region"
		:auto-hide-tabs="autoHideTabs"
		:content-size="contentSize"
		:is-drop-target="isDropTarget"
		:allowed-split-directions="allowedSplitDirections"
		@tab-mouse-up="(uri, wasActive) => $emit('tabMouseUp', uri, wasActive)"
	>
		<template #empty>
			<slot name="empty" />
		</template>
	</PanelContainer>

	<!-- branch node: render children with resize handles between them -->
	<div
		v-else
		ref="containerRef"
		class="flex h-full overflow-hidden"
		:class="isHorizontal ? 'flex-row' : 'flex-col'"
	>
		<template v-for="(child, index) in (node as BranchNode).children" :key="child.id">
			<!-- resize handle (between children, not before first) -->
			<div
				v-if="index > 0"
				class="split-resize-handle shrink-0"
				:class="isHorizontal ? 'split-resize-handle-horizontal' : 'split-resize-handle-vertical'"
				@pointerdown="onResizePointerDown($event, index - 1)"
			/>

			<!-- child node (recursive) -->
			<div :style="childStyle(index)" class="overflow-hidden">
				<SplitView
					:node="child"
					:panels="panels"
					:region="region"
					:auto-hide-tabs="autoHideTabs"
					:content-size="contentSize"
					:is-drop-target="isDropTarget"
					:allowed-split-directions="allowedSplitDirections"
					@tab-mouse-up="(uri, wasActive) => $emit('tabMouseUp', uri, wasActive)"
				>
					<template #empty>
						<slot name="empty" />
					</template>
				</SplitView>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
// recursive component self-reference
import { defineComponent } from 'vue';
export default defineComponent({ name: 'SplitView' });
</script>

<style scoped>
.split-resize-handle {
	position: relative;
	z-index: 20;
	flex-shrink: 0;
}

.split-resize-handle-horizontal {
	width: calc(var(--spacing) * 2);
	cursor: col-resize;
}

.split-resize-handle-horizontal::after {
	content: '';
	position: absolute;
	inset: 0 -2px;
}

.split-resize-handle-vertical {
	height: calc(var(--spacing) * 2);
	cursor: row-resize;
}

.split-resize-handle-vertical::after {
	content: '';
	position: absolute;
	inset: -2px 0;
}
</style>
